import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import { playNavHover, playNavClick } from "@/lib/sounds";
import { startKarnoffelMusic, stopKarnoffelMusic } from "@/lib/karnoffelMusic";
import { startChantMusic, stopChantMusic } from "@/lib/chantMusic";

// Per user iter M639/M640: nav now has a "Classic Games" dropdown
// between "The Tavern" and (formerly Karnöffel).  Karnöffel, Bong,
// 15-Tile Puzzle, Pente, Chess, and Checkers all live inside it.
// Angus and "A People's Chant" stay outside (they're a music-game
// and an upcoming original project, respectively).
const NAV_ITEMS = [
  { id: "home",      label: "Home / About Me",       route: "/" },
  { id: "diary",     label: "The Journal",           route: "/progress-diary" },
  { id: "tavern",    label: "The Tavern",            route: "/tavern" },
  {
    id: "classic-games",
    label: "Classic Games",
    group: true,
    children: [
      { id: "karnoffel", label: "Karn\u00f6ffel",    route: "/games/karnoffel" },
      { id: "wupong",    label: "Bong",              route: "/pong-tournament" },
      { id: "fifteen",   label: "15-Tile Puzzle",    route: "/games/fifteen" },
      { id: "pente",     label: "Pente",             route: "/games/pente" },
      { id: "chess",     label: "Chess",             route: "/games/chess" },
      { id: "checkers",  label: "Checkers",          route: "/games/checkers" },
    ],
  },
  { id: "angus",     label: "Angus and the Music Pantheon (cancelled)", route: "/games/angus" },
  { id: "chant",     label: "A People\u2019s Chant (cancelled)",   route: "/games/peoples-chant" },
  { id: "music",     label: "Music Portfolio",       route: "/music-portfolio" },
  { id: "gallery",   label: "\u2018The Cursed\u2019 Gallery", route: "/gallery" },
  { id: "gamedev",   label: "Game Dev Log (closed)",          route: "/gamedev-log" },
  { id: "playtesting", label: "Playtesting Log (closed)",     route: "/playtesting-log" },
  { id: "jokes",     label: "Jokes",                 route: "/jokes" },
  { id: "credits",   label: "Credits",               route: "/credits" },
  { id: "contact",   label: "Contacts and Other Portfolios", route: "/contact" },
];

/**
 * Shared left-rail navigation pane. Renders a compact column of links that
 * retint themselves to match the current page theme.
 *
 * Props:
 *   theme — "studio" | "tavern" | "wupong"
 *
 * Font size is now uniform across all pages (text-sm) per user spec.
 * Font family per theme:
 *   studio  → Playfair Display
 *   tavern  → IM Fell English (matches Tavern subtitle)
 *   wupong  → Bebas Neue (matches Wu-Pong brush headline family)
 */
export default function NavPane({ theme = "studio" }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Tracks which group rows are currently expanded (id → boolean).
  // Default-expand the "classic-games" dropdown whenever the visitor
  // is currently on one of its child routes so the active highlight
  // remains visible without forcing a manual click.
  const onClassicGamesRoute = [
    "/games/karnoffel", "/pong-tournament", "/games/fifteen",
    "/games/pente", "/games/chess", "/games/checkers",
  ].some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  const [openGroups, setOpenGroups] = useState({
    "classic-games": onClassicGamesRoute,
  });

  const toggleGroup = (id) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const isWu = theme === "wupong";
  const isTav = theme === "tavern";
  // Pente uses theme="studio" but its route should swap the NavPane font
  // to Lucida Console to match the rest of the Pente page (iter 33).
  // Also covers the multiplayer room route /games/pente/room/:key.
  const isPente = location.pathname.startsWith("/games/pente");

  // Accent color stays per-theme so the active-item indicator reads
  // correctly against each page's background.
  const accent = isWu ? "#C084FC" : isTav ? "#E5C870" : "#E5C870";
  const textColor = isWu ? "#F0E6FF" : isTav ? "#F5F1E8" : "#F5F1E8";
  const borderColor = isWu
    ? "rgba(192,132,252,0.22)"
    : isTav
    ? "rgba(229,200,112,0.18)"
    : "rgba(229,200,112,0.14)";
  const activePathSet = new Set([
    "/", "/gallery", "/music-portfolio", "/jokes", "/credits",
    "/tavern", "/tavern/big", "/tavern/host", "/tavern/join",
    "/pong-tournament", "/host", "/controller", "/game",
    "/games/chess", "/games/checkers", "/games/pente", "/games/fifteen",
    "/games/angus", "/games/peoples-chant", "/games/karnoffel",
    "/playtesting-log", "/gamedev-log", "/progress-diary", "/contact",
    "/bong",
  ]);

  const isActive = (route) => {
    if (route === "/") return location.pathname === "/";
    if (route === "/tavern") return location.pathname.startsWith("/tavern");
    if (route === "/pong-tournament")
      return ["/pong-tournament", "/host", "/controller", "/game"].some((p) =>
        location.pathname === p || location.pathname.startsWith(p + "/")
      );
    if (route === "/bong") return false; // route removed
    return location.pathname === route;
  };

  const nav = (route) => {
    if (!activePathSet.has(location.pathname)) return navigate(route);
    navigate(route);
  };

  // Uniform font size on every page.
  const sizeClass = "text-sm";

  // Per user iter M631: NavPane font / weight / letter-spacing are
  // now UNIFORM across every page so the navigation reads the same
  // wherever the visitor is. We pin everything to the homepage
  // (Times New Roman 600 / 0.01em) regardless of theme — the only
  // per-theme variable is the accent color of the active item.
  const headlineFont = "'Times New Roman', Times, serif";
  const letterSpacing = "0.01em";
  const fontWeight = 600;
  const fontStyle = "normal";
  // Silence the lint "unused" warnings — these flags remain in scope
  // in case future per-theme tweaks return.
  void isPente; void isWu; void isTav;

  return (
    <aside
      className="flex flex-col gap-1 pt-3 pb-3 px-2 md:px-3 nav-pane-sticky"
      style={{
        alignSelf: "flex-start",
        position: "sticky",
        // Per user iter M640: TopBar is permanently disabled, so the
        // sticky top offset is now 12px on EVERY page (matching the
        // homepage) — no more 60px gap on non-home routes.
        top: 12,
        maxHeight: "calc(100vh - 24px)",
        overflowY: "auto",
      }}
      data-testid="nav-pane"
    >
      <div
        className="mb-1 border-b"
        style={{ borderColor }}
      />
      {NAV_ITEMS.map((n) => {
        // Group row — toggles a dropdown beneath itself.
        if (n.group) {
          const open = !!openGroups[n.id];
          const Chevron = open ? ChevronDown : ChevronRight;
          return (
            <div key={n.id} data-testid={`navpane-group-${n.id}`}>
              <button
                onMouseEnter={playNavHover}
                onFocus={playNavHover}
                onClick={() => { playNavClick(); toggleGroup(n.id); }}
                data-silent="true"
                aria-expanded={open}
                className="group relative flex items-center justify-between gap-2 text-left px-2 py-2 transition-all rounded-sm w-full"
                style={{
                  background: "transparent",
                  borderLeft: "2px solid transparent",
                  color: textColor,
                  fontFamily: headlineFont,
                  letterSpacing,
                  fontWeight,
                }}
                data-testid={`navpane-${n.id}-toggle`}
              >
                <span
                  className={sizeClass}
                  style={{ fontFamily: headlineFont, letterSpacing, fontWeight, fontStyle }}
                >{n.label}</span>
                <Chevron
                  size={14}
                  className="shrink-0 transition-all"
                  style={{ color: accent, opacity: 0.7 }}
                />
              </button>
              {open && (
                <div
                  className="ml-3 mt-1 mb-1 flex flex-col gap-1 border-l"
                  style={{ borderColor }}
                  data-testid={`navpane-group-${n.id}-children`}
                >
                  {n.children.length === 0 ? (
                    <span
                      className="text-xs pl-3 py-1 italic"
                      style={{ color: textColor, opacity: 0.45, fontFamily: headlineFont }}
                      data-testid={`navpane-group-${n.id}-empty`}
                    >
                      (no items yet)
                    </span>
                  ) : (
                    n.children.map((child) => {
                      const active = isActive(child.route);
                      return (
                        <button
                          key={child.id}
                          onMouseEnter={playNavHover}
                          onFocus={playNavHover}
                          onClick={() => {
                            playNavClick();
                            if (child.route.startsWith("/games/karnoffel")) {
                              startKarnoffelMusic();
                            } else {
                              stopKarnoffelMusic();
                            }
                            if (child.route.startsWith("/games/peoples-chant")) {
                              startChantMusic();
                            } else {
                              stopChantMusic();
                            }
                            nav(child.route);
                          }}
                          data-silent="true"
                          className="group relative flex items-center justify-between gap-2 text-left pl-3 pr-2 py-1.5 transition-all rounded-sm"
                          style={{
                            background: active ? `${accent}14` : "transparent",
                            borderLeft: active ? `2px solid ${accent}` : "2px solid transparent",
                            color: textColor,
                            fontFamily: headlineFont,
                            letterSpacing,
                            fontWeight,
                          }}
                          data-testid={`navpane-${child.id}`}
                        >
                          <span
                            className={sizeClass}
                            style={{ fontFamily: headlineFont, letterSpacing, fontWeight, fontStyle }}
                          >{child.label}</span>
                          <ArrowRight
                            size={12}
                            className="shrink-0 transition-all"
                            style={{ color: accent, opacity: active ? 1 : 0.55 }}
                          />
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        }
        const active = isActive(n.route);
        return (
          <button
            key={n.id}
            onMouseEnter={playNavHover}
            onFocus={playNavHover}
            onClick={() => {
              playNavClick();
              // Per iter M637: kick the Karnöffel music start inside
              // the user-gesture (click) so stricter mobile/Safari
              // autoplay policies grant playback permission.
              if (n.route.startsWith("/games/karnoffel")) {
                startKarnoffelMusic();
              } else {
                stopKarnoffelMusic();
              }
              if (n.route.startsWith("/games/peoples-chant")) {
                startChantMusic();
              } else {
                stopChantMusic();
              }
              nav(n.route);
            }}
            data-silent="true"
            className="group relative flex items-center justify-between gap-2 text-left px-2 py-2 transition-all rounded-sm"
            style={{
              background: active ? `${accent}14` : "transparent",
              borderLeft: active ? `2px solid ${accent}` : "2px solid transparent",
              color: textColor,
              fontFamily: headlineFont,
              letterSpacing,
              fontWeight,
            }}
            data-testid={`navpane-${n.id}`}
          >
            <span
              className={sizeClass}
              style={{
                fontFamily: headlineFont,
                letterSpacing,
                fontWeight,
                fontStyle,
              }}
            >{n.label}</span>
            <ArrowRight
              size={13}
              className="shrink-0 transition-all"
              style={{
                color: accent,
                opacity: active ? 1 : 0.55,
                transform: active ? "translateX(2px)" : "translateX(0)",
              }}
            />
          </button>
        );
      })}
    </aside>
  );
}
